import { RadioGroup } from '@mui/material';
import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Category, ExceptionIgnoreScope, IssueScopeKind } from '../../../../../shared/issuesV2';
import {
  userHasPermissionToCreatePolicyType,
  userHasPermissionToResolveAnyRg,
  userHasPermissionToResolveIssueAnyProjectAndRg,
} from '../../../../helpers/permissions';
import { verify_subscription } from '../../../../utils';
import { FormGroup, icons, LabeledCheckbox } from '../../../generic';
import LabeledRadio from '../../../generic/Forms/LabeledRadio';
import Tooltip from '../../../generic/Tooltip';
import TooltipIcon from '../../../generic/TooltipIcon';
import type { Scope } from '../../../../../shared/issuesV2';
import type { PolicyType, SetState } from '../../../../types';

import { TOOLTIP_MAP } from './consts';

const premiumRequiredTooltip = 'Ignoring with a broader scope requires a premium subscription.';

type Props = {
  setIgnoreScope: SetState<ExceptionIgnoreScope>;
  ignoreScope: ExceptionIgnoreScope;
  issueCategory: Category;
  scope: Scope;
  willIncludeReleaseGroups: boolean;
  isCategoryException: boolean;
  onToggleAndReleaseGroups: () => void;
};

const IgnoreScopeOptions: VoidFunctionComponent<Props> = ({
  setIgnoreScope,
  ignoreScope,
  issueCategory,
  scope,
  willIncludeReleaseGroups,
  onToggleAndReleaseGroups,
  isCategoryException,
}) => {
  const projectOrReleaseGroupScope =
    scope.type === IssueScopeKind.ReleaseGroup ? ExceptionIgnoreScope.ReleaseGroup : ExceptionIgnoreScope.Project;

  let projectOrReleaseGroupMessage = 'this project';
  if (scope.type === IssueScopeKind.Global) {
    projectOrReleaseGroupMessage = 'the affected project(s)';
  } else if (scope.type === IssueScopeKind.ReleaseGroup) {
    projectOrReleaseGroupMessage = 'this release group';
  }

  const isPremium = verify_subscription('premium');
  const hasAnyRgPermission =
    userHasPermissionToResolveAnyRg(issueCategory) && isPremium;
  const hasPolicyPermission =
    userHasPermissionToCreatePolicyType(issueCategory.toUpperCase() as PolicyType) && isPremium;
  const hasAnyProjectAndRgPermission = userHasPermissionToResolveIssueAnyProjectAndRg(
    issueCategory,
  ) && isPremium;

  const isDisabled = !hasAnyRgPermission || ignoreScope !== ExceptionIgnoreScope.Project;

  useEffect(() => {
    // We want to unselect the "Include release groups" checkbox if the user has selected a scope that isn't Project.
    if (ignoreScope !== ExceptionIgnoreScope.Project && willIncludeReleaseGroups) {
      onToggleAndReleaseGroups();
    }
  }, [ignoreScope, willIncludeReleaseGroups, onToggleAndReleaseGroups]);

  return (
    <FormGroup legend="Where should it be ignored?" spacing="sm">
      <RadioGroup
        className="radio-filter-group"
        value={ignoreScope}
        onChange={(_event, value) => setIgnoreScope(value as ExceptionIgnoreScope)}
      >
        <div className="radio-filter-group-checks">
          <LabeledRadio
            label={
              <span>
                In {projectOrReleaseGroupMessage}{' '}
                <TooltipIcon
                  tooltip={`Ignores the selected issues in ${projectOrReleaseGroupMessage}.`}
                  icon={icons.circleQuestion}
                  id="project-rg-scope-tooltip"
                />
              </span>
            }
            value={projectOrReleaseGroupScope}
          />
          {scope.type !== IssueScopeKind.ReleaseGroup && (
            <div className="margin-left-md margin-bottom-sm and-release-groups-check">
              <LabeledCheckbox
                label={
                  // Tooltip for when the option is disabled.
                  <Tooltip
                    id="and-rgs-disabled-tooltip"
                    tooltip={isPremium ? 'You do not have permission to resolve issues in any Release Group.' : premiumRequiredTooltip}
                    showTooltip={!hasAnyRgPermission}
                  >
                    <span>
                      Include release groups{' '}
                      <TooltipIcon
                        tooltip={TOOLTIP_MAP[ExceptionIgnoreScope.ProjectAndReleaseGroups]}
                        icon={icons.circleQuestion}
                        id="and-rgs-tooltip"
                        // Only show if the option isn't disabled.
                        showTooltip={hasAnyRgPermission}
                      />
                    </span>
                  </Tooltip>
                }
                isChecked={willIncludeReleaseGroups}
                onChange={onToggleAndReleaseGroups}
                isDisabled={isDisabled}
              />
            </div>
          )}
          { issueCategory === Category.Licensing && !isCategoryException && (
            <LabeledRadio
              label={
                // Tooltip for when the option is disabled.
                <Tooltip
                  id="policy-scope-disabled-tooltip"
                  tooltip={isPremium ? 'You do not have permission to make changes to this policy.' : premiumRequiredTooltip}
                  showTooltip={!hasPolicyPermission}
                >
                  <span>
                    In the {issueCategory} policy{' '}
                    <TooltipIcon
                      tooltip={TOOLTIP_MAP[ExceptionIgnoreScope.Policy]}
                      icon={icons.circleQuestion}
                      id="policy-scope-tooltip"
                      // Only show if the option isn't disabled.
                      showTooltip={hasPolicyPermission}
                    />
                  </span>
                </Tooltip>
              }
              value={ExceptionIgnoreScope.Policy}
              isDisabled={!hasPolicyPermission}
            />
          )}
          <LabeledRadio
            label={
              // Tooltip for when the option is disabled.
              <Tooltip
                id="policy-scope-disabled-tooltip"
                tooltip={isPremium ? 'You do not have permission to resolve issues in any Project and any Release Group.' : premiumRequiredTooltip}
                showTooltip={!hasAnyProjectAndRgPermission}
              >
                <span>
                  Globally{' '}
                  <TooltipIcon
                    tooltip={TOOLTIP_MAP[ExceptionIgnoreScope.Organization]}
                    icon={icons.circleQuestion}
                    id="global-scope-tooltip"
                    // Only show if the option isn't disabled.
                    showTooltip={hasAnyProjectAndRgPermission}
                  />
                </span>
              </Tooltip>
            }
            value={ExceptionIgnoreScope.Organization}
            isDisabled={!hasAnyProjectAndRgPermission}
          />
        </div>
      </RadioGroup>
    </FormGroup>
  );
};

export default IgnoreScopeOptions;
